import { useMemo, useState } from "react";
import { AlertTriangle, FolderTree, Pencil, Plus, Trash2 } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { DeepLinkMissing } from "@/components/DeepLinkMissing";
import { useDeepLink } from "@/lib/use-deep-link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/features/auth/useAuth";
import { CategoryEditDialog } from "@/features/materials/CategoryEditDialog";
import { deleteCategoryCascade } from "@/features/materials/categories-api";
import { useCategories, useMaterials } from "@/features/materials/useMaterials";
import { describeWriteError } from "@/features/users/permissions";
import { CategoryIndex } from "@/lib/category-ref";
import { categoryGroupNames, categorySections, groupKey } from "@/lib/category-groups";
import { COST_TYPES, COST_TYPE_LABELS } from "@/lib/constants";
import type { CostType, MaterialCategory } from "@/lib/types";
import { usePagination } from "@/lib/use-pagination";

/** Value of the group filter that keeps only the categories without a group. */
const NO_GROUP = "__none__";

/** Stage 4b: categories of the cost-item catalog, with their attributes. */
export function CategoriesPage() {
  const { user, profile } = useAuth();
  const { categories, isLoading, error } = useCategories();
  // Only to show how many cost items use a category before deleting it.
  const { materials } = useMaterials(user?.uid);

  const [search, setSearch] = useState("");
  const [costType, setCostType] = useState<CostType | "">("");
  // "" = all groups, NO_GROUP = only the ungrouped ones, otherwise a group key.
  const [group, setGroup] = useState("");
  const [editing, setEditing] = useState<MaterialCategory | null>(null);
  const [isCreating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<MaterialCategory | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  // `/categories?edit=<id>` (links from "Несоответствия") opens that category's editor.
  const deepLink = useDeepLink("edit", categories, !isLoading, setEditing);

  const actor = {
    uid: user?.uid ?? "",
    label: profile?.email ?? user?.email ?? user?.uid ?? "",
  };

  const groupNames = useMemo(() => categoryGroupNames(categories), [categories]);
  // A selected group that no longer exists (renamed away) falls back to «Все группы».
  const activeGroup =
    group === "" || group === NO_GROUP || groupNames.some((name) => groupKey(name) === group)
      ? group
      : "";

  // Listed group by group (alphabetically, ungrouped last), each in the usual order.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = categories
      .filter((item) => (costType === "" ? true : item.costType === costType))
      .filter((item) =>
        activeGroup === ""
          ? true
          : activeGroup === NO_GROUP
            ? groupKey(item.groupName) === ""
            : groupKey(item.groupName) === activeGroup,
      )
      .filter((item) =>
        needle.length === 0
          ? true
          : `${item.name} ${item.groupName}`.toLowerCase().includes(needle),
      );
    return categorySections(filtered).flatMap((section) => section.categories);
  }, [categories, search, costType, activeGroup]);

  const pager = usePagination(visible);

  // Counted by reference (id, else name for older items), so a renamed category keeps its count.
  const usageByKey = useMemo(() => {
    const index = new CategoryIndex(categories);
    const counts = new Map<string, number>();
    for (const item of materials) {
      const key = index.key(item.categoryId, item.category);
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return { index, counts };
  }, [categories, materials]);
  const usageOf = (category: MaterialCategory) =>
    usageByKey.counts.get(usageByKey.index.key(category.id, category.name)) ?? 0;

  const handleDelete = async () => {
    if (!deleting) return;
    setWriteError(null);
    try {
      await deleteCategoryCascade(deleting.id);
      setDeleting(null);
    } catch (caught) {
      setWriteError(describeWriteError(caught));
    }
  };

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Категории пунктов затрат</h1>
          <p className="text-muted-foreground text-sm">
            Справочник категорий и их атрибутов. Общий для всех пользователей,
            менять может только администратор.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus />
          Добавить
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pager.resetPage();
          }}
          placeholder="Поиск по названию или группе"
          className="min-w-56 flex-1"
          aria-label="Поиск категорий"
        />
        <Select
          value={costType}
          onChange={(event) => {
            setCostType(event.target.value as CostType | "");
            pager.resetPage();
          }}
          className="w-44"
          aria-label="Фильтр по типу затрат"
        >
          <option value="">Все типы затрат</option>
          {COST_TYPES.map((type) => (
            <option key={type} value={type}>
              {COST_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
        <Select
          value={activeGroup}
          onChange={(event) => {
            setGroup(event.target.value);
            pager.resetPage();
          }}
          className="w-48"
          aria-label="Фильтр по группе"
        >
          <option value="">Все группы</option>
          {groupNames.map((name) => (
            <option key={name} value={groupKey(name)}>
              {name}
            </option>
          ))}
          <option value={NO_GROUP}>Без группы</option>
        </Select>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Справочник не загружен</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <DeepLinkMissing
        missingId={deepLink.missingId}
        what="Категория"
        onDismiss={deepLink.dismiss}
      />

      {writeError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Действие не выполнено</AlertTitle>
          <AlertDescription>{writeError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="py-0">
        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
              <FolderTree className="size-8" />
              {categories.length === 0
                ? "Категорий пока нет."
                : "Ничего не найдено."}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Группа</TableHead>
                    <TableHead>Название</TableHead>
                    <TableHead>Порядок</TableHead>
                    <TableHead>Тип затрат</TableHead>
                    <TableHead>Пунктов</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="text-muted-foreground text-sm">
                        {category.groupName || "—"}
                      </TableCell>
                      <TableCell className="font-medium">
                        {category.name}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {category.orderIndex}
                      </TableCell>
                      <TableCell className="text-sm">
                        {COST_TYPE_LABELS[category.costType]}
                      </TableCell>
                      <TableCell className="text-sm">
                        {usageOf(category)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(category)}
                        >
                          <Pencil />
                          Изменить
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(category)}
                          aria-label={`Удалить категорию ${category.name}`}
                        >
                          <Trash2 />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={pager.page}
                pageCount={pager.pageCount}
                pageSize={pager.pageSize}
                total={pager.total}
                firstShown={pager.firstShown}
                lastShown={pager.lastShown}
                onPage={pager.setPage}
                onPageSize={pager.setPageSize}
                itemsLabel="категорий"
              />
            </>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        Удаление категории удаляет и все её атрибуты одной атомарной операцией.
        Пункты затрат при этом не удаляются — они хранят название категории
        строкой, поэтому у них останется значение, которого больше нет в
        справочнике (в карточке пункта оно подписано «нет в справочнике»). Тип
        затрат категории подсказывает, какие пункты в неё попадают, но не
        ограничивает их: у пункта свой собственный тип.
      </p>

      {isCreating ? (
        <CategoryEditDialog
          category={null}
          categories={categories}
          actor={actor}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <CategoryEditDialog
          category={editing}
          categories={categories}
          actor={actor}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {deleting ? (
        <Dialog open onOpenChange={(open) => !open && setDeleting(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Удалить категорию?</DialogTitle>
              <DialogDescription>
                «{deleting.name}» и все её атрибуты будут удалены. Пунктов
                затрат с этой категорией: {usageOf(deleting)} — они останутся,
                но их категория исчезнет из справочника.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Отмена
              </Button>
              <Button variant="destructive" onClick={() => void handleDelete()}>
                <Trash2 />
                Удалить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
